import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiInstance } from '../../axiosApi';

export const dispositionListGet = createAsyncThunk('setting/disposition', async (params) => {
  return await apiInstance.get('/disposition', { params });
});

export const dispositionOptionsGet = createAsyncThunk(
  'setting/disposition-options',
  async (params) => {
    return await apiInstance.get('/disposition', { params });
  }
);

export const dispositionListGetById = createAsyncThunk('setting/disposition-id', async (id) => {
  return await apiInstance.get(`/disposition/${id}`);
});

export const dispositionListGetByProcess = createAsyncThunk(
  'setting/disposition-process-id',
  async (params) => {
    return await apiInstance.get(`/disposition`, { params });
  }
);

export const dispositionAdd = createAsyncThunk('setting/disposition-add', async (payload) => {
  return await apiInstance.post('/disposition', payload);
});

export const dispositionCopy = createAsyncThunk(
  'setting/disposition/copy-disposition',
  async (payload) => {
    return await apiInstance.post('/disposition/copy-disposition', payload);
  }
);

export const breakCopy = createAsyncThunk('setting/breaks/copy-breaks', async (payload) => {
  return await apiInstance.post('/breaks/copy-breaks', payload);
});

export const dispositionUpdate = createAsyncThunk('setting/disposition-update', async (payload) => {
  const { id, ...rest } = payload;
  return await apiInstance.put(`/disposition/${id}`, rest);
});

export const dispositionDelete = createAsyncThunk('setting/disposition-delete', async (id) => {
  return await apiInstance.delete(`/disposition/${id}`);
});

export const aiDispositionListGet = createAsyncThunk('setting/ai-disposition', async (params) => {
  return await apiInstance.get('/callex-ai-disposition/disposition', { params });
});

export const aiDispositionAdd = createAsyncThunk('setting/ai-disposition-add', async (payload) => {
  return await apiInstance.post('/callex-ai-disposition/disposition', payload);
});

export const aiDispositionUpdate = createAsyncThunk(
  'setting/ai-disposition-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/callex-ai-disposition/disposition/${id}`, rest);
  }
);

export const aiDispositionDelete = createAsyncThunk('setting/ai-disposition-delete', async (id) => {
  return await apiInstance.delete(`/callex-ai-disposition/disposition/${id}`);
});

export const pbxDtmfListGet = createAsyncThunk('setting/pbx-dtmf-list', async (params) => {
  return await apiInstance.get('/easy-pbx-dtmf/', { params });
});

export const subDispositionListGet = createAsyncThunk('setting/sub_disposition', async (params) => {
  return await apiInstance.get('/sub_disposition', { params });
});

export const subDispositionDelete = createAsyncThunk(
  'setting/sub_disposition-delete',
  async (id) => {
    return await apiInstance.delete(`/sub_disposition/${id}`);
  }
);

export const subDispositionListGetById = createAsyncThunk(
  'setting/sub_disposition-id',
  async (id) => {
    return await apiInstance.get(`/sub_disposition/${id}`);
  }
);

export const subDispositionAdd = createAsyncThunk(
  'setting/sub_disposition-add',
  async (payload) => {
    return await apiInstance.post('/sub_disposition', payload);
  }
);

export const subDispositionUpdate = createAsyncThunk(
  'setting/sub-disposition-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/sub_disposition/${id}`, rest);
  }
);

export const dispositionListGetByDispo = createAsyncThunk(
  'setting/disposition-dispo-id',
  async (params) => {
    return await apiInstance.get(`/sub_disposition`, { params });
  }
);

export const subSubDispositionListGet = createAsyncThunk(
  'setting/sub_sub_disposition',
  async (params) => {
    return await apiInstance.get('/sub_sub_disposition', { params });
  }
);

export const subSubDispositionDelete = createAsyncThunk(
  'setting/sub_sub_disposition-delete',
  async (id) => {
    return await apiInstance.delete(`/sub_sub_disposition/${id}`);
  }
);

export const subSubDispositionListGetById = createAsyncThunk(
  'setting/sub_sub_disposition-id',
  async (id) => {
    return await apiInstance.get(`/sub_sub_disposition/${id}`);
  }
);

export const subSubDispositionAdd = createAsyncThunk(
  'setting/sub_sub_disposition-add',
  async (payload) => {
    return await apiInstance.post('/sub_sub_disposition', payload);
  }
);

export const subSubDispositionUpdate = createAsyncThunk(
  'setting/sub_sub_disposition-update',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/sub_sub_disposition/${id}`, rest);
  }
);

const initialState = {
  dispositionList: null,
  aiDispositionList: null,
  subDispositionList: null,
  subSubDispositionList: null,
  pbxDtmfList: null
};

const settingSlice = createSlice({
  name: 'setting',
  initialState,
  reducers: {
    clearSettingSlice: () => initialState
  },
  extraReducers: (builder) => {
    builder.addCase(dispositionListGet.fulfilled, (state, action) => {
      state.dispositionList = action?.payload;
    });
    builder.addCase(subDispositionListGet.fulfilled, (state, action) => {
      state.subDispositionList = action?.payload;
    });
    builder.addCase(subSubDispositionListGet.fulfilled, (state, action) => {
      state.subSubDispositionList = action?.payload;
    });
    builder.addCase(aiDispositionListGet.fulfilled, (state, action) => {
      state.aiDispositionList = action?.payload;
    });
    builder.addCase(pbxDtmfListGet.fulfilled, (state, action) => {
      state.pbxDtmfList = action?.payload;
    });
  }
});

export default settingSlice.reducer;
export const { clearSettingSlice } = settingSlice.actions;

export const selectDispositionList = (state) => state.setting.dispositionList;
export const useDispositionList = () => {
  const dispositionList = useSelector(selectDispositionList);
  return useMemo(() => dispositionList, [dispositionList]);
};

export const selectSubDispositionList = (state) => state.setting.subDispositionList;
export const useSubDispositionList = () => {
  const subDispositionList = useSelector(selectSubDispositionList);
  return useMemo(() => subDispositionList, [subDispositionList]);
};

export const selectSubSubDispositionList = (state) => state.setting.subSubDispositionList;
export const useSubSubDispositionList = () => {
  const subSubDispositionList = useSelector(selectSubSubDispositionList);
  return useMemo(() => subSubDispositionList, [subSubDispositionList]);
};

export const selectAiDispositionList = (state) => state.setting.aiDispositionList;
export const useAiDispositionList = () => {
  const aiDispositionList = useSelector(selectAiDispositionList);
  return useMemo(() => aiDispositionList, [aiDispositionList]);
};

export const selectPbxDtmfList = (state) => state.setting.pbxDtmfList;
export const usePbxDtmfList = () => {
  const pbxDtmfList = useSelector(selectPbxDtmfList);
  return useMemo(() => pbxDtmfList, [pbxDtmfList]);
};
